import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiUsers } from 'react-icons/fi';
import { AiOutlineClose } from 'react-icons/ai';
import AxiosUrl from '../AxiosUrl';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './Customer.module.css';

function Customer() {
    const uid = localStorage.getItem('id');
    const [customers, setCustomers] = useState([]);
    const [refresh, setRefresh] = useState(0);

    // Pagination state
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const customersPerPage = 10;

    // Search and filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [searchDebounce, setSearchDebounce] = useState(null);

    const [loading, setLoading] = useState(true);

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [deletingCustomer, setDeletingCustomer] = useState(null);

    // Form states for create
    const [createForm, setCreateForm] = useState({
        name: '',
        mobile: '',
        address: '',
        location: ''
    });

    // Form states for edit
    const [editForm, setEditForm] = useState({
        name: '',
        mobile: '',
        address: '',
        location: ''
    });

    // Fetch customers with search and filters
    const fetchCustomers = useCallback(
        async (currentPage = 1, search = '', status = '') => {
            setLoading(true);
            try {
                const response = await AxiosUrl.get('/search-customers', {
                    params: {
                        uid,
                        searchQuery: search,
                        status: status,
                        page: currentPage,
                        limit: customersPerPage,
                    },
                });

                if (response.data.success) {
                    setCustomers(response.data.customers);
                    setTotalPages(response.data.pagination.totalPages);
                    setTotalCount(response.data.pagination.totalCount);
                }
            } catch (error) {
                toast.error('Failed to load customers');
                console.error(error);
            } finally {
                setLoading(false);
            }
        },
        [uid]
    );

    useEffect(() => {
        fetchCustomers(page, searchQuery, statusFilter);
    }, [page, refresh]);

    // Search handler with debouncing
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);

        if (searchDebounce) clearTimeout(searchDebounce);

        const timeout = setTimeout(() => {
            setPage(1);
            fetchCustomers(1, value, statusFilter);
        }, 300);

        setSearchDebounce(timeout);
    };

    const clearSearch = () => {
        setSearchQuery('');
        setPage(1);
        fetchCustomers(1, '', statusFilter);
    };

    // Status filter handler
    const handleStatusChange = (e) => {
        const value = e.target.value;
        setStatusFilter(value);
        setPage(1);
        fetchCustomers(1, searchQuery, value);
    };

    // Pagination handler
    const handlePageChange = (newPage) => {
        setPage(newPage);
        fetchCustomers(newPage, searchQuery, statusFilter);
    };

    // Delete customer handlers
    const openDeleteModal = (customer) => {
        setDeletingCustomer(customer);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setDeletingCustomer(null);
    };

    const handleDeleteConfirm = async () => {
        if (!deletingCustomer) return;

        try {
            const response = await AxiosUrl.post(`/delete-customer/${uid}`, { cid: deletingCustomer._id });

            if (response.data.acknowledged) {
                toast.success('Customer deleted successfully.');
                closeDeleteModal();
                setRefresh((prev) => prev + 1);
            }
        } catch (error) {
            toast.error('Failed to delete the customer.');
            console.error(error);
        }
    };

    // Create modal handlers
    const openCreateModal = () => {
        setCreateForm({ name: '', mobile: '', address: '', location: '' });
        setShowCreateModal(true);
    };

    const closeCreateModal = () => {
        setShowCreateModal(false);
        setCreateForm({ name: '', mobile: '', address: '', location: '' });
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();

        if (!createForm.name || !createForm.mobile || !createForm.address || !createForm.location) {
            toast.error('All fields are required!');
            return;
        }

        if (createForm.mobile.length !== 11) {
            toast.error('১১ সংখ্যার মোবাইল নম্বর লিখুন');
            return;
        }

        try {
            const response = await AxiosUrl.post(`/create-customer/${uid}`, createForm);

            if (response.data === 'Customer Already Exist') {
                toast.error(`Already exist with ${createForm.mobile}`);
            } else if (response.data === 'Customer Successful') {
                toast.success('Customer created successfully');
                closeCreateModal();
                setRefresh((prev) => prev + 1);
            }
        } catch (error) {
            toast.error('Failed to create customer');
            console.error(error);
        }
    };

    // Edit modal handlers
    const openEditModal = (customer) => {
        setEditingCustomer(customer);
        setEditForm({
            name: customer.name,
            mobile: customer.mobile,
            address: customer.address,
            location: customer.location === true ? 'dhaka' : 'outside'
        });
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setEditingCustomer(null);
        setEditForm({ name: '', mobile: '', address: '', location: '' });
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        if (!editForm.name || !editForm.mobile || !editForm.address || !editForm.location) {
            toast.error('All fields are required!');
            return;
        }

        if (editForm.mobile.length !== 11) {
            toast.error('১১ সংখ্যার মোবাইল নম্বর লিখুন');
            return;
        }

        try {
            const response = await AxiosUrl.post(`/edit-customer/${uid}`, {
                ...editForm,
                old_mobile: editingCustomer.mobile
            });

            toast.success('Customer updated successfully');
            closeEditModal();
            setRefresh((prev) => prev + 1);
        } catch (error) {
            toast.error('Failed to update customer');
            console.error(error);
        }
    };

    return (
        <>
            <ToastContainer />
            <div className={styles.container}>
                <div className={styles.content}>
                    {/* Header */}
                    <div className={styles.header}>
                        <div className={styles.headerLeft}>
                            <h1 className={styles.title}>Customers</h1>
                            <p className={styles.subtitle}>Manage your customer database</p>
                        </div>
                        <button className={styles.addBtn} onClick={openCreateModal}>
                            <i className="fas fa-plus"></i>
                            Add Customer
                        </button>
                    </div>

                    {/* Search and Filter Bar */}
                    <div className={styles.filtersSection}>
                        <div className={styles.searchBox}>
                            <FiSearch className={styles.searchIcon} />
                            <input
                                type="text"
                                placeholder="Search by name or mobile..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className={styles.searchInput}
                            />
                            {searchQuery && (
                                <button className={styles.clearBtn} onClick={clearSearch}>
                                    <AiOutlineClose />
                                </button>
                            )}
                        </div>

                        <select
                            value={statusFilter}
                            onChange={handleStatusChange}
                            className={styles.filterSelect}
                        >
                            <option value="">All Status</option>
                            <option value="1">Active</option>
                            <option value="0">Inactive</option>
                        </select>

                        <div className={styles.customerCount}>
                            {totalCount} customers
                        </div>
                    </div>

                    {/* Table */}
                    {loading ? (
                        <div style={{ borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', background: 'white', marginBottom: '24px' }}>
                            {/* Skeleton header row */}
                            <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', gap: '16px' }}>
                                <div style={{ flex: 1 }}><div className={styles.skeleton} style={{ width: '110px', height: '12px' }}></div></div>
                                <div style={{ width: '120px' }}><div className={styles.skeleton} style={{ width: '60px', height: '12px' }}></div></div>
                                <div style={{ flex: 1 }}><div className={styles.skeleton} style={{ width: '70px', height: '12px' }}></div></div>
                                <div style={{ width: '100px' }}><div className={styles.skeleton} style={{ width: '70px', height: '12px' }}></div></div>
                                <div style={{ width: '90px' }}><div className={styles.skeleton} style={{ width: '55px', height: '12px' }}></div></div>
                                <div style={{ width: '100px', textAlign: 'center' }}><div className={styles.skeleton} style={{ width: '60px', height: '12px', margin: '0 auto' }}></div></div>
                            </div>
                            {/* Skeleton data rows */}
                            {[...Array(8)].map((_, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', borderBottom: i < 7 ? '1px solid #f3f4f6' : 'none', gap: '16px' }}>
                                    <div style={{ flex: 1 }}><div className={styles.skeleton} style={{ width: `${100 + (i % 3) * 25}px`, height: '16px' }}></div></div>
                                    <div style={{ width: '120px' }}><div className={styles.skeleton} style={{ width: '100px', height: '14px' }}></div></div>
                                    <div style={{ flex: 1 }}><div className={styles.skeleton} style={{ width: `${140 + (i % 2) * 50}px`, height: '14px' }}></div></div>
                                    <div style={{ width: '100px' }}><div className={styles.skeleton} style={{ width: '64px', height: '24px', borderRadius: '12px' }}></div></div>
                                    <div style={{ width: '90px' }}><div className={styles.skeleton} style={{ width: '60px', height: '24px', borderRadius: '12px' }}></div></div>
                                    <div style={{ width: '100px', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                        <div className={styles.skeleton} style={{ width: '32px', height: '32px', borderRadius: '6px' }}></div>
                                        <div className={styles.skeleton} style={{ width: '32px', height: '32px', borderRadius: '6px' }}></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className={styles.tableContainer}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>CUSTOMER NAME</th>
                                        <th>MOBILE</th>
                                        <th>ADDRESS</th>
                                        <th>LOCATION</th>
                                        <th>STATUS</th>
                                        <th>ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {customers.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className={styles.emptyCell}>
                                                <div className={styles.emptyIcon}><FiUsers /></div>
                                                <h3>No customers found</h3>
                                                <p>
                                                    {searchQuery || statusFilter
                                                        ? 'Try adjusting your search or filters'
                                                        : 'Get started by adding your first customer'}
                                                </p>
                                            </td>
                                        </tr>
                                    ) : (
                                        customers.map((customer) => (
                                            <tr key={customer._id}>
                                                <td className={styles.nameCell}>{customer.name}</td>
                                                <td>{customer.mobile}</td>
                                                <td className={styles.addressCell}>{customer.address}</td>
                                                <td>
                                                    <span className={styles.locationBadge}>
                                                        {customer.location === true ? 'Dhaka' : 'Outside'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={customer.status === '0' ? styles.statusInactive : styles.statusActive}>
                                                        {customer.status === '0' ? 'Inactive' : 'Active'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className={styles.actionBtns}>
                                                        <button
                                                            className={styles.editBtn}
                                                            onClick={() => openEditModal(customer)}
                                                            title="Edit customer"
                                                        >
                                                            <i className="fas fa-edit"></i>
                                                        </button>
                                                        <button
                                                            className={styles.deleteBtn}
                                                            onClick={() => openDeleteModal(customer)}
                                                            title="Delete customer"
                                                        >
                                                            <i className="fas fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && totalPages > 1 && (
                        <div className={styles.pagination}>
                            <button
                                className={styles.pageBtn}
                                onClick={() => handlePageChange(1)}
                                disabled={page === 1}
                            >
                                &laquo;
                            </button>
                            <button
                                className={styles.pageBtn}
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page === 1}
                            >
                                &lsaquo;
                            </button>

                            {[...Array(totalPages)].map((_, index) => {
                                const pageNum = index + 1;
                                if (
                                    pageNum === 1 ||
                                    pageNum === totalPages ||
                                    (pageNum >= page - 1 && pageNum <= page + 1)
                                ) {
                                    return (
                                        <button
                                            key={pageNum}
                                            className={`${styles.pageBtn} ${page === pageNum ? styles.active : ''}`}
                                            onClick={() => handlePageChange(pageNum)}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                } else if (pageNum === page - 2 || pageNum === page + 2) {
                                    return <span key={pageNum} className={styles.ellipsis}>...</span>;
                                }
                                return null;
                            })}

                            <button
                                className={styles.pageBtn}
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page === totalPages}
                            >
                                &rsaquo;
                            </button>
                            <button
                                className={styles.pageBtn}
                                onClick={() => handlePageChange(totalPages)}
                                disabled={page === totalPages}
                            >
                                &raquo;
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Customer Modal */}
            {showCreateModal && (
                <div className={styles.modalOverlay} onClick={closeCreateModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>Create Customer</h2>
                            <button className={styles.closeBtn} onClick={closeCreateModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <form onSubmit={handleCreateSubmit} className={styles.modalForm}>
                            <div className={styles.formGroup}>
                                <label>Customer Name *</label>
                                <input
                                    type="text"
                                    value={createForm.name}
                                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                                    placeholder="Enter customer name"
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Mobile Number *</label>
                                <input
                                    type="text"
                                    value={createForm.mobile}
                                    onChange={(e) => setCreateForm({ ...createForm, mobile: e.target.value })}
                                    placeholder="01XXXXXXXXX"
                                    maxLength="11"
                                    required
                                />
                                <small>Enter 11 digit mobile number</small>
                            </div>

                            <div className={styles.formGroup}>
                                <label>Address *</label>
                                <input
                                    type="text"
                                    value={createForm.address}
                                    onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })}
                                    placeholder="Road# 000, House# 000, Apt# A2, Gulshan Dhaka"
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Location *</label>
                                <input
                                    type="text"
                                    value={createForm.location}
                                    onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
                                    placeholder="dhaka or outside"
                                    required
                                />
                            </div>

                            <div className={styles.modalActions}>
                                <button type="button" className={styles.cancelBtn} onClick={closeCreateModal}>
                                    Cancel
                                </button>
                                <button type="submit" className={styles.submitBtn}>
                                    Create Customer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Customer Modal */}
            {showEditModal && (
                <div className={styles.modalOverlay} onClick={closeEditModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>Edit Customer</h2>
                            <button className={styles.closeBtn} onClick={closeEditModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <form onSubmit={handleEditSubmit} className={styles.modalForm}>
                            <div className={styles.formGroup}>
                                <label>Customer Name *</label>
                                <input
                                    type="text"
                                    value={editForm.name}
                                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                    placeholder="Enter customer name"
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Mobile Number *</label>
                                <input
                                    type="text"
                                    value={editForm.mobile}
                                    onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
                                    placeholder="01XXXXXXXXX"
                                    maxLength="11"
                                    required
                                />
                                <small>Enter 11 digit mobile number</small>
                            </div>

                            <div className={styles.formGroup}>
                                <label>Address *</label>
                                <input
                                    type="text"
                                    value={editForm.address}
                                    onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                                    placeholder="Road# 000, House# 000, Apt# A2, Gulshan Dhaka"
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Location *</label>
                                <input
                                    type="text"
                                    value={editForm.location}
                                    onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                                    placeholder="dhaka or outside"
                                    required
                                />
                            </div>

                            <div className={styles.modalActions}>
                                <button type="button" className={styles.cancelBtn} onClick={closeEditModal}>
                                    Cancel
                                </button>
                                <button type="submit" className={styles.submitBtn}>
                                    Update Customer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Customer Modal */}
            {showDeleteModal && deletingCustomer && (
                <div className={styles.modalOverlay} onClick={closeDeleteModal}>
                    <div className={styles.deleteModalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.deleteModalHeader}>
                            <div className={styles.deleteIcon}>
                                <i className="fas fa-exclamation-triangle"></i>
                            </div>
                            <div className={styles.deleteHeaderText}>
                                <h2>Delete Customer</h2>
                                <p>This action cannot be undone</p>
                            </div>
                        </div>

                        <div className={styles.deleteModalBody}>
                            <p>Are you sure you want to delete this customer? All customer data will be permanently removed.</p>

                            <div className={styles.deleteCustomerInfo}>
                                <p><strong>Name:</strong> {deletingCustomer.name}</p>
                                <p><strong>Mobile:</strong> {deletingCustomer.mobile}</p>
                                <p><strong>Address:</strong> {deletingCustomer.address}</p>
                            </div>
                        </div>

                        <div className={styles.deleteModalActions}>
                            <button className={styles.cancelBtn} onClick={closeDeleteModal}>
                                Cancel
                            </button>
                            <button className={styles.deleteConfirmBtn} onClick={handleDeleteConfirm}>
                                <i className="fas fa-trash"></i> Delete Customer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Customer;
